import type { VmtChartViewId } from './vmtNarrative';

export type VmtStructuredMethodology = {
  primarySources: string[];
  calculation: string[];
  limitations: string[];
  technicalDetails: string[];
};

const LDV_HDV_SHARED_TECH = [
  'Replica PRIVATE_AUTO and ON_DEMAND_AUTO annual VMT queries',
  'emfac2025_fleet (city HDV totals)',
  'emfac2021_county_filtered (county HDV backcast ratios)',
  'population table (per-capita denominators)',
  'VMTChart.tsx; VMTComparisonChart.tsx',
];

export function buildVmtStructuredMethodology(viewId: VmtChartViewId): VmtStructuredMethodology {
  if (viewId === 'comparison') {
    return {
      primarySources: [
        'Light-duty VMT follows the ClimatePlans LDV series described above.',
        'Heavy-duty VMT uses CARB EMFAC2025 Fleet city heavy-duty totals for available recent years and county-ratio reconstruction for earlier years where implemented.',
      ],
      calculation: [
        'LDV and HDV values are compared only for compatible/common years.',
        'LDV/HDV shares are calculated only when both series represent compatible geography and year totals.',
      ],
      limitations: [
        'LDV and HDV use different source and reconstruction methods.',
        'Differences between the two series should not be interpreted as though they were produced by one identical measurement system.',
        'The chart describes travel activity; emissions also depend on vehicle technology and emissions per mile.',
      ],
      technicalDetails: LDV_HDV_SHARED_TECH,
    };
  }

  return {
    primarySources: [
      'Replica annual vehicle miles traveled for PRIVATE_AUTO and ON_DEMAND_AUTO for recent local light-duty travel estimates.',
      'CARB EMFAC2025 county and Fleet indicators used in historical reconstruction/backcast ratios.',
      'CARB EMFAC2021 Fleet indicators only where the corresponding 2025 series is unavailable.',
      'Caltrans Average Annual Daily Traffic (AADT) indicators used in the combined 2015–2022 backcast.',
      'ClimatePlans population data for same-year per-capita normalization.',
      'For a California per-capita comparison: Replica California geography using the same PRIVATE_AUTO + ON_DEMAND_AUTO modes and same reporting year.',
    ],
    calculation: [
      'Recent local light-duty VMT is the sum of Replica PRIVATE_AUTO and ON_DEMAND_AUTO annual VMT for the selected place.',
      'Daily VMT per resident = annual light-duty VMT ÷ same-year population ÷ 365.',
      'For 2015–2022, the default Combined reconstruction applies the arithmetic mean of available Caltrans AADT, EMFAC2025 county, EMFAC2025 Fleet, and EMFAC2021 Fleet year-over-year ratios to the 2015 baseline.',
      'For 2005–2014, the default Combined reconstruction uses the mean of EMFAC2025 county and population ratios anchored to 2015. Alternate user-selected methods use the corresponding available series.',
      'California comparison is shown only when metric definition, modes, year, unit, and denominator are compatible.',
    ],
    limitations: [
      'Replica values are modeled travel estimates, not direct citywide traffic counts.',
      'Earlier values are reconstructed/backcast estimates.',
      'Backcast method selections can change historical estimates and therefore historical trend calculations.',
      'Per-capita values are community averages and do not represent the distance traveled by a typical individual.',
    ],
    technicalDetails: [
      'Replica OD VMT tables (PRIVATE_AUTO, ON_DEMAND_AUTO)',
      'emfac2025_county_filtered; emfac2025_fleet; emfac2021_county_filtered',
      'Caltrans AADT ingest for combined backcast ratios',
      'population table',
      'VMTChart.tsx',
    ],
  };
}
